'use client';

import type { ASPRefreshUserInfoResponse, MemberLoginResponse } from '@module/apis/client-side/member/type';
import SettingConfig from '@module/config/setting';
import { useASPRefreshUserInfoQuery, useMemberLoginInfoQuery } from '@module/queries/member/memberQueryHooks';
import TimeConstants from '@module/utils/item';
import type { CustomUseQueryOptions } from '../../queries/type';
import { aspCond } from '../conditions/condition';

type LoginInfoQueryResponse = MemberLoginResponse | ASPRefreshUserInfoResponse;

const useGetLoginInfoQuery = (options?: CustomUseQueryOptions<LoginInfoQueryResponse, Error>) => {
  const memberLoginInfoQuery = useMemberLoginInfoQuery({}, {
    staleTime: TimeConstants.ONE_MINUTE,
    ...options,
    enabled: SettingConfig.isFanza ? false : options?.enabled,
  } as CustomUseQueryOptions<MemberLoginResponse, Error>);
  /**
   * fanza에서는 ASP 포인트 갱신 이유로 /member/login_info가 아닌 /member/refresh_user_info를 사용.
   */
  const aspRefreshUserInfoQuery = useASPRefreshUserInfoQuery({
    staleTime: TimeConstants.ONE_MINUTE,
    ...options,
    retry: false,
    enabled: SettingConfig.isFanza ? options?.enabled : false,
  } as CustomUseQueryOptions<ASPRefreshUserInfoResponse, Error>);

  const {
    data: loginInfo,
    isLoading: isLoginInfoLoading,
    isFetching: isLoginInfoFetching,
    refetch: refetchLoginInfo,
    isFetched,
    isError,
  } = SettingConfig.isFanza ? aspRefreshUserInfoQuery : memberLoginInfoQuery;

  const userInfo = loginInfo?.loginInfo?.userInfo;
  const isUserInfo = !!userInfo;
  const isLogin = !!userInfo?.isLogin;
  const isAdult = !!userInfo?.isAdult;
  const isAuthDone = userInfo?.authYN === 'Y';
  const siteMode = loginInfo?.loginInfo?.siteMode;
  const coinHave = userInfo?.coinHave;
  const wasLive = !!userInfo?.liveFirstDateTime && userInfo.liveFirstDateTime !== '0000-00-00 00:00:00';

  let isPostAvailable = true;
  if (aspCond.if('new-asp')) {
    isPostAvailable = SettingConfig.newASPPreset?.aspInfo?.postYN === 'Y' && userInfo?.purchaseUser === 'Y';
  }

  return {
    rawLoginState: userInfo?.isLogin,
    loginInfo,
    isLogin,
    isAdult,
    isUserInfo,
    userInfo,
    isLoginInfoLoading,
    isLoginInfoFetching,
    isFetched,
    isAuthDone,
    refetchLoginInfo,
    siteMode,
    coinHave,
    wasLive,
    isError,
    isPostAvailable,
  };
};

export default useGetLoginInfoQuery;
